import React, { useState, useEffect } from "react";
import { Mail, Upload, X, Eye } from "lucide-react";
import handlebarsService from "../../../services/handlebarsService";
import { templatesList } from "../../../constants/templates";
import { EmailTemplate } from "../../../types/EmailTemplate";
import { RichTextEditor } from "../../ui/RichTextEditor";
import { enrichTemplateDataAsync, BACKEND_PLACEHOLDERS_HELP } from "../../../utils/templateDataEnricher";

interface EmailFormWithTemplateProps {
  userCount: number;
  isBroadcast: boolean;
  userIds: string[];
  onToggleMode: () => void;
  onUserIdsChange: (userIds: string[]) => void;
  onSend: (subject: string, htmlMessage: string, attachments: File[]) => Promise<void>;
  sending: boolean;
}

export const EmailFormWithTemplate: React.FC<EmailFormWithTemplateProps> = ({
  userCount,
  isBroadcast,
  userIds,
  onToggleMode,
  onUserIdsChange,
  onSend,
  sending,
}) => {
  const [subject, setSubject] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("default");
  const [templateData, setTemplateData] = useState<Record<string, any>>({});
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState<string[]>([]);
  const [htmlPreview, setHtmlPreview] = useState<string>("");
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const selectedTemplate: EmailTemplate | undefined = templatesList.find((t) => t.id === selectedTemplateId);

  useEffect(() => {
    if (selectedTemplate) {
      const initialData: Record<string, any> = {};
      selectedTemplate.fields.forEach((field) => {
        initialData[field.name] = field.defaultValue || "";
      });
      setTemplateData(initialData);
    }
  }, [selectedTemplateId]);

  const handleFieldChange = (fieldName: string, value: string) => {
    setTemplateData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleImageUpload = (fieldName: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("L'image ne doit pas dépasser 5 MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateData((prev) => ({ ...prev, [fieldName]: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);

    if (totalSize > 20 * 1024 * 1024) {
      alert("Les pièces jointes ne doivent pas dépasser 20 MB au total");
      return;
    }

    const oversizedFiles = files.filter((file) => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert("Chaque fichier ne doit pas dépasser 10 MB");
      return;
    }

    setAttachments((prev) => [...prev, ...files]);

    files.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAttachmentPreviews((prev) => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      } else {
        setAttachmentPreviews((prev) => [...prev, ""]);
      }
    });
  };

  const removeAttachment = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);

    const newPreviews = [...attachmentPreviews];
    newPreviews.splice(index, 1);
    setAttachmentPreviews(newPreviews);
  };

  const handlePreview = async () => {
    if (!selectedTemplate) return;

    setPreviewLoading(true);
    try {
      // Données pour l'aperçu avec valeurs fictives
      const dataToPreview = await enrichTemplateDataAsync(selectedTemplateId, {
        ...(selectedTemplate.previewData || {}),
        ...templateData,
      });

      let html = await handlebarsService.render(selectedTemplate.templateFile, dataToPreview);

      // Remplacer les placeholders pour l'aperçu
      html = html.replace(/__GREETING__/g, "Bonjour Jean Dupont,");
      html = html.replace(/__UNSUBSCRIBE_URL__/g, "https://site.com/unsubscribe?token=exemple");
      html = html.replace(/__USER_EMAIL__/g, "jean.dupont@email.com");

      setHtmlPreview(html);
      setShowPreview(true);
    } catch (error) {
      console.error("Erreur lors de l'aperçu:", error);
      alert("Erreur lors de la génération de l'aperçu");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject) {
      alert("Le sujet est requis");
      return;
    }

    if (!isBroadcast && userIds.length === 0) {
      alert("Veuillez sélectionner au moins un utilisateur");
      return;
    }

    if (!selectedTemplate) {
      alert("Veuillez sélectionner un modèle");
      return;
    }

    const missingFields = selectedTemplate.fields
      .filter((field) => field.required && !templateData[field.name])
      .map((field) => field.label);

    if (missingFields.length > 0) {
      alert(`Champs requis manquants : ${missingFields.join(", ")}`);
      return;
    }

    try {
      // Ajouter currentYear aux données
      const dataWithYear = await enrichTemplateDataAsync(selectedTemplateId, templateData);

      // Générer le HTML avec les placeholders
      const htmlMessage = await handlebarsService.render(selectedTemplate.templateFile, dataWithYear);

      // Appeler onSend
      await onSend(subject, htmlMessage, attachments);
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      alert("Erreur lors de la génération de l'email");
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          {isBroadcast
            ? "Email à tous les utilisateurs"
            : `Email (${userCount} utilisateur${userCount > 1 ? "s" : ""})`}
        </h3>
        {isBroadcast && (
          <button
            onClick={onToggleMode}
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Envoyer à un seul utilisateur
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Sujet */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Sujet <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Sujet de l'email"
            required
          />
        </div>

        {/* Sélecteur de template */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Modèle d'email</label>
          <select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500"
          >
            {templatesList.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          {selectedTemplate && (
            <p className="mt-2 p-3 text-sm text-gray-600 bg-gray-50 rounded-lg border border-gray-200">
              {selectedTemplate.description}
            </p>
          )}
        </div>

        <div className="p-3 text-sm text-blue-900 bg-blue-50 rounded-lg border border-blue-200">
          {BACKEND_PLACEHOLDERS_HELP}
        </div>

        {/* Champs dynamiques du template */}
        {selectedTemplate && (
          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium text-gray-700">Contenu du modèle</h4>
            {selectedTemplate.fields.map((field) => (
              <div key={field.name}>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>

                {field.type === "image" ? (
                  <div className="space-y-2">
                    {templateData[field.name] && (
                      <div className="relative inline-block">
                        <img
                          src={templateData[field.name]}
                          alt="Aperçu"
                          className="h-32 w-auto rounded-lg border object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handleFieldChange(field.name, "")}
                          className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(field.name, e)}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500"
                    />
                    <p className="text-xs text-gray-500">Format recommandé : 600x300px, max 5MB</p>
                  </div>
                ) : field.type === "textarea" ? (
                  <RichTextEditor
                    value={templateData[field.name] || ""}
                    onChange={(value) => handleFieldChange(field.name, value)}
                    placeholder={field.placeholder}
                  />
                ) : field.type === "select" ? (
                  <select
                    value={templateData[field.name] || field.defaultValue || ""}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500"
                    required={field.required}
                  >
                    {(field.options || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type === "url" ? "url" : "text"}
                    value={templateData[field.name] || ""}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pièces jointes */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Pièces jointes (optionnel)</label>
          <div className="p-6 text-center rounded-lg border-2 border-gray-300 border-dashed transition-colors hover:border-green-500">
            <input
              type="file"
              multiple
              onChange={handleAttachmentUpload}
              className="hidden"
              id="attachment-upload-template"
            />
            <label htmlFor="attachment-upload-template" className="flex flex-col items-center cursor-pointer">
              <Upload className="mb-2 w-12 h-12 text-gray-400" />
              <p className="text-sm font-medium text-gray-600">Cliquez pour télécharger des fichiers</p>
              <p className="mt-1 text-xs text-gray-500">Max 10MB par fichier, 20MB total</p>
            </label>
          </div>

          {attachments.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-gray-700">{attachments.length} fichier(s) attaché(s)</p>
              {attachments.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    {file.type.startsWith("image/") && attachmentPreviews[index] && (
                      <img src={attachmentPreviews[index]} alt="Aperçu" className="w-10 h-10 rounded object-cover" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    className="p-1 text-red-600 hover:text-red-700 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bouton Aperçu */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handlePreview}
            disabled={previewLoading}
            className="flex gap-2 justify-center items-center py-2 px-4 font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors"
          >
            <Eye className="w-4 h-4" />
            {previewLoading ? "Génération..." : "Aperçu"}
          </button>
        </div>

        {/* Modal d'aperçu */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
              <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
                <h3 className="text-lg font-semibold">Aperçu de l'email</h3>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-4">
                <iframe
                  srcDoc={htmlPreview}
                  title="Aperçu de l'email"
                  className="w-full min-h-[500px] border-0 rounded"
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          </div>
        )}

        {/* Bouton d'envoi */}
        <button
          type="submit"
          disabled={sending}
          className="flex gap-2 justify-center items-center py-3 w-full font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {sending ? (
            <>
              <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
              Envoi en cours...
            </>
          ) : (
            <>
              <Mail className="w-5 h-5" />
              {isBroadcast
                ? "Envoyer à tous les utilisateurs"
                : `Envoyer à ${userCount} utilisateur${userCount > 1 ? "s" : ""}`}
            </>
          )}
        </button>
      </form>
    </div>
  );
};
